import { RouteObject } from 'react-router-dom';
import React, { Suspense } from 'react';
import MainLayout from '../Components/Layout/MainLayout';
import LoadingSpinner from '../Components/Loading/LoadingSpinner';

// Lazy load components
const SignIn = React.lazy(() => import('../Components/SignIn/SignIn'));
const VerifyEmail = React.lazy(() => import('../Components/Register/VerifyEmail'));
const VerifyOtp = React.lazy(() => import('../Components/Register/VerifyOtp'));
const EmailVerifySuccess = React.lazy(() => import('../Components/Register/EmailVerifySuccess'));
const AddProfile = React.lazy(() => import('../Components/Register/AddProfile'));
const ForgotPassword = React.lazy(() => import('../Components/SignIn/ForgotPassword'));
const ResetPassword = React.lazy(() => import('../Components/SignIn/ResetPassword'));
const PasswordChangeSuccess = React.lazy(() => import('../Components/SignIn/PasswordChangeSuccess'));
const CreateKpi = React.lazy(() => import('../Components/KPIReports/createKpi'));
const KpiList = React.lazy(() => import('../Components/KPIReports/KpiList'));
const KpiReport = React.lazy(() => import('../Components/ViewReports/kpiReport'));
const AddProfileSuccess = React.lazy(() => import('../Components/Register/AddProfileSuccess'));
const ViewKpi = React.lazy(() => import('../Components/KPIReports/viewKpi'));
const ViewKpiReport = React.lazy(() => import('../Components/ViewReports/viewKpiReport'));
const GenerateChart = React.lazy(() => import('../Components/GenerateChart/generateChart'));
const ViewChart = React.lazy(() => import('../Components/GenerateChart/ViewChart'));
const UsersList = React.lazy(() => import('../Components/Users/usersList'));
const UserFeedback = React.lazy(() => import('../Components/Feedback/userFeedback'));
const ShowCopiedImage = React.lazy(() => import('../Components/GenerateChart/showCopiedImage'));
const ScoreCard = React.lazy(() => import('../Components/Scorecard'));
const CreateDashboard = React.lazy(() => import('../Components/Dashboard/CreateDashboard'));
const ViewDashboard = React.lazy(() => import('../Components/Dashboard/ViewDashboard'));
const NotFound = React.lazy(() => import('../Components/404/notFound'));
const UserProfile = React.lazy(() => import('../Components/Users/UserProfile/UserProfile'));

// Wrapper component for lazy loading
const LazyLoad = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
);

export const ROUTES = {
  AUTH: {
    LOGIN: '/login',
    REGISTER: '/register',
    VERIFY_OTP: '/verify-otp',
    EMAIL_VERIFY_SUCCESS: '/email-verify-success',
    ADD_PROFILE: '/add-profile',
    REGISTER_SUCCESS: '/register-success',
    FORGOT_PASSWORD: '/forgot-password',
    RESET_PASSWORD: '/reset-password',
    PASSWORD_CHANGE_SUCCESS: '/password-change-success',
  },
  KPI: {
    LIST: '/kpi-list',
    CREATE: '/create-kpi',
    VIEW: '/view-kpi/:id',
    EDIT: '/edit-kpi/:id',
    REPORT: '/kpi-report',
    VIEW_REPORT: '/kpi-report/view/:id',
  },
  DASHBOARD: {
    CREATE: '/create-dashboard',
    VIEW: '/view-dashboard',
  },
  CHART: {
    GENERATE: '/generate-chart',
    VIEW: '/view-chart',
    IMAGE: '/image/:id',
  },
  USER: {
    LIST: '/users',
    PROFILE: '/user-profile',
    FEEDBACK: '/feedback',
  },
  SCORECARD: {
    GENERATE: '/generate-scorecard',
  },
  NOT_FOUND: '*',
} as const;

export const publicRoutes: RouteObject[] = [
  {
    path: ROUTES.AUTH.LOGIN,
    element: (
      <LazyLoad>
        <SignIn />
      </LazyLoad>
    ),
  },
  {
    path: ROUTES.AUTH.REGISTER,
    element: (
      <LazyLoad>
        <VerifyEmail />
      </LazyLoad>
    ),
  },
  {
    path: ROUTES.AUTH.VERIFY_OTP,
    element: (
      <LazyLoad>
        <VerifyOtp />
      </LazyLoad>
    ),
  },
  {
    path: ROUTES.AUTH.EMAIL_VERIFY_SUCCESS,
    element: (
      <LazyLoad>
        <EmailVerifySuccess />
      </LazyLoad>
    ),
  },
  {
    path: ROUTES.AUTH.ADD_PROFILE,
    element: (
      <LazyLoad>
        <AddProfile />
      </LazyLoad>
    ),
  },
  {
    path: ROUTES.AUTH.REGISTER_SUCCESS,
    element: (
      <LazyLoad>
        <AddProfileSuccess />
      </LazyLoad>
    ),
  },
  {
    path: ROUTES.AUTH.FORGOT_PASSWORD,
    element: (
      <LazyLoad>
        <ForgotPassword />
      </LazyLoad>
    ),
  },
  {
    path: ROUTES.AUTH.RESET_PASSWORD,
    element: (
      <LazyLoad>
        <ResetPassword />
      </LazyLoad>
    ),
  },
  {
    path: ROUTES.AUTH.PASSWORD_CHANGE_SUCCESS,
    element: (
      <LazyLoad>
        <PasswordChangeSuccess />
      </LazyLoad>
    ),
  },
];

export const protectedRoutes: RouteObject[] = [
  {
    path: ROUTES.KPI.LIST,
    element: (
      <MainLayout>
        <LazyLoad>
          <KpiList />
        </LazyLoad>
      </MainLayout>
    ),
  },
  {
    path: ROUTES.KPI.CREATE,
    element: (
      <MainLayout>
        <LazyLoad>
          <CreateKpi />
        </LazyLoad>
      </MainLayout>
    ),
  },
  {
    path: ROUTES.KPI.VIEW,
    element: (
      <MainLayout>
        <LazyLoad>
          <ViewKpi />
        </LazyLoad>
      </MainLayout>
    ),
  },
  {
    path: ROUTES.KPI.EDIT,
    element: (
      <MainLayout>
        <LazyLoad>
          <CreateKpi />
        </LazyLoad>
      </MainLayout>
    ),
  },
  {
    path: ROUTES.KPI.REPORT,
    element: (
      <MainLayout>
        <LazyLoad>
          <KpiReport />
        </LazyLoad>
      </MainLayout>
    ),
  },
  {
    path: ROUTES.KPI.VIEW_REPORT,
    element: (
      <MainLayout>
        <LazyLoad>
          <ViewKpiReport />
        </LazyLoad>
      </MainLayout>
    ),
  },
  {
    path: ROUTES.SCORECARD.GENERATE,
    element: (
      <MainLayout>
        <LazyLoad>
          <ScoreCard />
        </LazyLoad>
      </MainLayout>
    ),
  },
  {
    path: ROUTES.DASHBOARD.CREATE,
    element: (
      <MainLayout>
        <LazyLoad>
          <CreateDashboard />
        </LazyLoad>
      </MainLayout>
    ),
  },
  {
    path: ROUTES.DASHBOARD.VIEW,
    element: (
      <MainLayout>
        <LazyLoad>
          <ViewDashboard />
        </LazyLoad>
      </MainLayout>
    ),
  },
  {
    path: ROUTES.CHART.GENERATE,
    element: (
      <MainLayout>
        <LazyLoad>
          <GenerateChart />
        </LazyLoad>
      </MainLayout>
    ),
  },
  {
    path: ROUTES.USER.LIST,
    element: (
      <MainLayout>
        <LazyLoad>
          <UsersList />
        </LazyLoad>
      </MainLayout>
    ),
  },
  {
    path: ROUTES.USER.FEEDBACK,
    element: (
      <MainLayout>
        <LazyLoad>
          <UserFeedback />
        </LazyLoad>
      </MainLayout>
    ),
  },
  {
    path: ROUTES.CHART.VIEW,
    element: (
      <MainLayout>
        <LazyLoad>
          <ViewChart />
        </LazyLoad>
      </MainLayout>
    ),
  },
  {
    path: ROUTES.USER.PROFILE,
    element: (
      <MainLayout>
        <LazyLoad>
          <UserProfile />
        </LazyLoad>
      </MainLayout>
    ),
  },
  {
    path: ROUTES.CHART.IMAGE,
    element: (
      <MainLayout>
        <LazyLoad>
          <ShowCopiedImage />
        </LazyLoad>
      </MainLayout>
    ),
  },
  {
    path: ROUTES.NOT_FOUND,
    element: (
      <LazyLoad>
        <NotFound />
      </LazyLoad>
    ),
  },
]; 